import React, { useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import nextId from "react-id-generator";

// import { ReactComponent as BuyButton } from "../../../../../assets/images/BuyButton.svg";

import type {
  OptionTimeframe,
  OrderType,
  TradeDirection,
} from "../../../../../components/DashBoard/GTT/gttTypes";
import type { ChargesSummaryVisible } from "../../../../../slices/ordersSlice";
import type { RootState } from "../../../../../store";

import { ReactComponent as Donut } from "../../../../../assets/icons/Donut.svg";

import GenericInput from "../../../../../components/DashBoard/GTT/common/GTTGenericInput";
import { Button, Form } from "react-bootstrap";
import ToggleButton from "../../../../../components/DashBoard/GTT/common/GTTTrigger/ToggleGroup";

import { zodResolver } from "@hookform/resolvers/zod";

import "./ordersBody.scss";

import { gtt_l_text } from "../../constants";
import { orderSchema } from "./orderSchema";
import BalanceDisplay from "./BalanceDisplay";
import ActionButton from "./ActionButton";

import type { Order } from "../../../../../models/orders";
import { useDispatch, useSelector } from "react-redux";

import {
  OpenFutureChain,
  setCurrentTab,
} from "../../../../../slices/optionChainSlice";

import {
  resetChargesSummaryVisible,
  setChargesSummaryVisible,
  setChargesSummaryVisibleKey,
  setOrderSummary,
} from "../../../../../slices/ordersSlice";

import useLiveQuotesGeneric from "../../../../../hooks/socket/useLiveQuoteGeneric";

import {
  getFinalVariety,
  getOrderByLots,
  indexSymbolsForMarginReduction,
  orderDataFormer,
} from "../../../../../utils/orders.helper";

import useOrderRequest from "../../../../../hooks/api/create/useCreateOrders";
import useLiveResolveSymbol from "../../../../../hooks/socket/useLiveResolveSymbol";
import updateOrders from "../../../../../hooks/api/put/updateOrders";
import useReducedMargin from "../../../../../hooks/api/get/getReducedMargin";

import type { ReducedMarginProps } from "../../../../../hooks/api/get/getReducedMargin";
import ReducedMarginCheckbox from "./ReducedMargin";
import useMarginsBasket from "../../../../../hooks/api/get/getMargin";
import { OptionTimeframePayload } from "../../../../../components/DashBoard/GTT/gtt.helper";
import { getSortName } from "../../../../../utils/getShortName";
import { DataFeedInstance } from "../../../../../chart-datafeed/udf-compatible-datafeed";
import { ResolveSymbolInfo } from "../../../../../models/symbolData";

export interface OrdersProps {
  CloseGttModal: () => void;
  memorizedLtp: number;
  tradeDirection: TradeDirection;
  symbol: string;
  orderState: ChargesSummaryVisible;
  instrumentName?: string;
  token: number;
  segMentId: number;
  lotSize: number;
  tick: number;
  exchange: string;
  isIceBerg: boolean;
  onClick?: () => void;
  thisOrderTypeRLOrMKT?: OrderFormData["orderType"]; // "Limit", "Market"
  thisTriggedPrice?: number;
  thisValidity?: OrderFormData["validity"]; // "DAY", "IOC", "AMO"
  thisLotSize?: number;
  thisStoplossTriggeredPrice?: number;
  thisGTTData?: Order["gtt"];
  isEditable: boolean;
  thisStopLoss: boolean;
  thisId?: string;
  thisDisclosedQuantity?: number;
  setPrice: React.Dispatch<React.SetStateAction<number>>;
  selectedTab: OptionTimeframe;
}

export type OrderFormData = z.infer<typeof orderSchema>;

const OrdersBody: React.FC<OrdersProps> = ({
  CloseGttModal,
  memorizedLtp,
  tradeDirection,
  symbol,
  orderState,
  token,
  segMentId,
  lotSize,
  instrumentName,
  exchange,
  tick,
  isIceBerg,
  onClick,
  thisOrderTypeRLOrMKT,
  thisTriggedPrice,
  thisValidity,
  thisLotSize,
  thisStoplossTriggeredPrice,
  thisGTTData,
  isEditable,
  thisStopLoss,
  thisId,
  thisDisclosedQuantity,
  selectedTab,
  setPrice,
}) => {
  const memerizedTick = useMemo(() => tick / 100, []);

  const listenerGuid = nextId();
  const liveQuotes = useLiveQuotesGeneric(token, segMentId, listenerGuid); // Live Socket Data Hook
  const { symbolInfo } = useLiveResolveSymbol(liveQuotes?.description ?? "");
  const { data, loading, error, mutate } = useOrderRequest();
  const {
    data: updateData,
    loading: updateLoading,
    error: updateError,
    mutate: updateMutate,
  } = updateOrders();

  /**
   * We need to check if the symbol is in the list of symbols that we want to show the checkbox or not
   * If the symbol is in the list, then we need to show the checkbox
   * Else, we don't need to show the checkbox
   */
  const isMarginReductionCheckboxVisible =
    tradeDirection === "Sell" &&
    indexSymbolsForMarginReduction.includes(symbolInfo?.odin_symbol ?? "") &&
    ["CE", "PE"].includes(symbolInfo?.odin_option_type ?? "") &&
    !isEditable &&
    memorizedLtp > 50;

  const [ask, qunatity] =
    tradeDirection === "Buy"
      ? [liveQuotes?.ask, liveQuotes?.ask_qty]
      : [liveQuotes?.bid, liveQuotes?.bid_qty];

  const [low, high] = useMemo(() => {
    return liveQuotes?.dpr?.split("-").map(Number) ?? [0, 0];
  }, [liveQuotes?.dpr]);

  const dispatch = useDispatch();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<OrderFormData>({
    resolver: zodResolver(orderSchema),
    mode: "onChange", // Fixed syntax: changed = to :
    defaultValues: {
      memorizedLTP: 0,
      memerizedTick: 0,
      isReducedMargin: false,
      low: 0,
      high: 0,
      isIceBerg: false,

      lots: "1",
      price: "",
      triggerPrice: "",
      orderType: "Market",
      isStopLoss: false,
      validity: "DAY",
      tradeDirection: tradeDirection,
      type: "", // non selected by default

      isGTTStopLoss: true, // If any of GGT-M || GGT-L is selected, then enable the stoploss and target
      gttStoplossValue: "",
      isGTTTarget: true, // If any of GGT-M || GGT-L is selected, then enable the stoploss and target
      gttTargetValue: "",
    },
  });

  // This use Effetc it to update setOrderSummary
  useEffect(() => {
    dispatch(
      setOrderSummary({
        stockExchnage: exchange,
        mktinstrumentType:
          (instrumentName && getSortName(instrumentName)) || "",
        marketSegmentId: segMentId,
        tradeType: selectedTab,
        transactionType: tradeDirection === "Buy" ? "BUY" : "SELL",
        lotSize: lotSize,
        price:
          watch("orderType") === "Market"
            ? Number(watch("memorizedLTP"))
            : Number(watch("price")),
      })
    );
  }, [
    exchange,
    lotSize,
    instrumentName,
    segMentId,
    selectedTab,
    tradeDirection,
    watch("orderType"),
    watch("price"),
  ]);

  const { data: reducedMarginData } = useReducedMargin({
    memorizedLTP: watch("memorizedLTP"),
    isChecked: watch("isReducedMargin"),
    optionType:
      symbolInfo?.odin_option_type as ReducedMarginProps["optionType"],

    symbolInfo: symbolInfo?.odin_symbol,
    odin_token: symbolInfo?.odin_token,
    odin_exchange: symbolInfo?.odin_exchange,
    odin_expiry_date: symbolInfo?.odin_expiry_date,
  });

  const requitedToken = reducedMarginData?.requitedToken;
  const ltp = reducedMarginData?.ltp;

  useEffect(() => {
    trigger([
      "price",
      "high",
      "low",
      "orderType",
      "validity",
      "type",
      "isGTTStopLoss",
      "isGTTTarget",
      "tradeDirection",
      "isStopLoss",
      "triggerPrice",
      "isIceBerg",
      "numberOfLegs",
      "lots",
    ]);
    // if the price had been changed
    if (watch("price")) setPrice(+watch("price"));
  }, [
    watch("price"),
    watch("orderType"),
    watch("high"),
    watch("low"),
    watch("validity"),
    watch("type"),
    watch("isGTTStopLoss"),
    watch("isGTTTarget"),
    watch("tradeDirection"),
    watch("isStopLoss"),
    watch("triggerPrice"),
    watch("isIceBerg"),
    watch("numberOfLegs"),
    watch("lots"),
    watch("memorizedLTP"),
  ]);

  const summaryData = useSelector(
    (state: RootState) => state.orders.chargesSummaryVisible
  );

  const { orderType: userOrderTypePreference, smartOrderPreferences } =
    useSelector((state: RootState) => state.orderPreference);

  const isIceBergVisible = smartOrderPreferences?.iceBerg && isIceBerg;

  // const isSplitOrderFormRemoteConfig = smartOrderPreferences?.splitOrder;

  useEffect(() => {
    setValue("tradeDirection", tradeDirection);
    // Clear GTT values when direction changes to prevent invalid states
    setValue("gttStoplossValue", "");
    setValue("gttTargetValue", "");
    setValue("price", memorizedLtp.toFixed(2));
    setValue(
      "orderType",
      userOrderTypePreference === "market" ? "Market" : "Limit"
    );
    setValue("memerizedTick", memerizedTick);
    setValue("low", low);
    setValue("high", high);
    setValue("memorizedLTP", memorizedLtp);

    // Start For Edit Values
    if (thisOrderTypeRLOrMKT) {
      setValue("orderType", thisOrderTypeRLOrMKT);
    }

    if (thisTriggedPrice) {
      setValue("price", `${thisTriggedPrice}`);
    }

    if (thisValidity) {
      setValue("validity", thisValidity);
    }

    if (thisLotSize) {
      setValue("lots", `${thisLotSize}`);
    }

    if (thisGTTData) {
      if (thisGTTData.sl_variety) {
        if (thisGTTData.sl_variety === "RL") setValue("type", "GTT-L");
        if (thisGTTData.sl_variety === "RL-MKT") setValue("type", "GTT-M");

        // now we are Shure we must have either of one value [ profit_trigger_percent | sl_trigger_percent ]
        if (thisGTTData.profit_trigger_percent) {
          setValue("isGTTTarget", true);
          setValue("gttTargetValue", `${thisGTTData.profit_trigger_percent}`);
        }

        if (thisGTTData.sl_trigger_percent) {
          setValue("isGTTStopLoss", true);
          setValue("gttStoplossValue", `${thisGTTData.sl_trigger_percent}`);
        }
      }
    }

    setValue("isStopLoss", thisStopLoss); // we need to set this always in Create and Edit both
    if (thisStopLoss) {
      // we need to set memorized LTP in case of stoploss
      setValue("triggerPrice", memorizedLtp.toFixed(2));
    }
    // Edit Values End Here

    if (thisStoplossTriggeredPrice) {
      setValue("isStopLoss", true);
      setValue("triggerPrice", `${thisStoplossTriggeredPrice}`);
    }

    // now base on smartOrderPreferences which we need to set the value

    // For GTT-M || GTT-L || as we already had it in beginng so we  may we don't need to pass a dependency array
    if (smartOrderPreferences?.gtt) {
      const isLimit = smartOrderPreferences.gttOrderPreference.limit; // It may be true or false
      const isMarket = smartOrderPreferences.gttOrderPreference.market; // It may be true or false
      // if both are false do nothing

      if (isLimit) setValue("type", "GTT-L");
      if (isMarket) setValue("type", "GTT-M");

      // now we need to set stoploss and target value
      const isStopLossGTT = smartOrderPreferences.gttOrderPreference.stoploss; // It may be true or false
      const isTargetGTT = smartOrderPreferences.gttOrderPreference.target;

      setValue("isGTTStopLoss", isStopLossGTT);
      setValue("isGTTTarget", isTargetGTT);
      setValue("isIceBerg", !!isIceBergVisible);
    }
  }, [
    tradeDirection,
    setValue,
    memorizedLtp,
    userOrderTypePreference,
    memerizedTick,
    low,
    high,
    isIceBergVisible,
    thisOrderTypeRLOrMKT,
    thisLotSize,
    thisValidity,
    thisGTTData,
    thisStopLoss,
  ]);

  const orderType = watch("orderType");
  const isStopLoss = watch("isStopLoss");
  const lots = watch("lots");

  const type = watch("type"); // Track the type value

  const isGTTStopLoss = watch("isGTTStopLoss");
  const isGTTTarget = watch("isGTTTarget");

  const onSubmit = async (data: OrderFormData) => {
    // Handle order update (PUT request)
    if (isEditable) {
      const payload = {
        ...data,
        disclosed_quantity: thisDisclosedQuantity,
        order_id: thisId, // we will pass this
        odin_exchange: symbolInfo?.odin_exchange,
        odin_lot_size: symbolInfo?.odin_lot_size,
        market_segment_id: symbolInfo?.odin_market_segment_id,
      };
      
      const updateOrder = await updateMutate(payload, {
        onSuccess: (response) => {
          // console.log("Order updated successfully:", response);
        },
        onError: (errorMessage) => {
          // console.error("Error updating order:", errorMessage);
        },
      }).then((res: any) => {
        CloseGttModal();
      });
      
      // console.log("Update Done ", updateOrder);
      return;
    }
    
    // For new orders, check if symbol needs surveillance confirmation
    const processNewOrder = async (customSymbolInfo?: ResolveSymbolInfo) => {
      // Use provided symbolInfo or fall back to the one in outer scope
      const currentSymbolInfo = customSymbolInfo || symbolInfo;
      
      // Helper function to place orders - reused in multiple places
      const placeOrder = async (orderData: any) => {
        try {
          const responseData = await mutate(orderData).then(() => {
            CloseGttModal();
          });
          // console.log("Order placed successfully:", responseData);
        } catch (errorMessage) {
          // console.error("Error placing order:", errorMessage);
        }
      };
      
      // Helper function to format lots based on order type and exchange
      const formatLots = (isIceBerg: boolean, lotsValue: string, exchange?: string): string => {
        if (isIceBerg && ["NSE_FO", "BSE_FO"].includes(exchange ?? "")) {
          return `${+lotsValue * lotSize}`;
        } 
        return exchange ==='MCX_FO' ? lotsValue :`${+lotsValue * lotSize}`;
      };
      
      // Helper function to handle the actual order placement logic
      const processOrder = async (useReducedMargin: boolean, processingSymbolInfo: ResolveSymbolInfo) => {
        // Handle reduced margin case
        if (useReducedMargin) {
          // it mean we need to make a post request for this order
          const lots = processingSymbolInfo.odin_market_segment_id === 5 ? data.lots : (Number(data.lots) * (Number(processingSymbolInfo. odin_lot_size)));
          const splittedArray = getOrderByLots(
            processingSymbolInfo?.odin_symbol ?? "",
            +lots
          );
          
          if (!ltp) return;
          
          if (splittedArray.length === 1) {
            // Single order case
            const isIceBerg = data.type === "Iceberg";
            const lots = formatLots(isIceBerg, data.lots, processingSymbolInfo?.odin_exchange);
            
            // TODO: we are working
            const finalData = orderDataFormer({
              data: { ...data, lots: lots },
              token: requitedToken ?? 0,
              orderState: {
                ...orderState,
                buySell: "BUY",
                memorizedLTP: ltp,
                price: ltp,
              },
            });
            
            await placeOrder(finalData);
          } else {
            // Multiple orders case
            const orderPromises = splittedArray.map((lots) => {
              const finalData = orderDataFormer({
                data: { ...data, lots: `${lots * lotSize}` },
                token: requitedToken ?? 0,
                orderState: {
                  ...orderState,
                  buySell: "BUY",
                  memorizedLTP: ltp,
                  price: ltp,
                },
              });
              
              // console.log("finalData",finalData.legs)
              return placeOrder(finalData); // Return the promise
            });
            
            // If you want the requests to be executed sequentially, await them in a for loop
            for (let promise of orderPromises) {
              await promise;
            }
            
            // If you want them to run concurrently, use Promise.all
            await Promise.all(orderPromises).then(() => {
              CloseGttModal();
            });
          }
          //  TODO: we can make it single function
        }
        
        // The below code is necessary for the case as it always going to be triggered
        const lots = processingSymbolInfo.odin_market_segment_id === 5 ? data.lots : (Number(data.lots) * (Number(processingSymbolInfo. odin_lot_size)));
        const splittedArray = getOrderByLots(
          processingSymbolInfo?.odin_symbol ?? "",
          +lots
        );
        
        if (splittedArray.length === 1) {
          // Single order case
          const isIceBerg = data.type === "Iceberg";
          const lots = formatLots(isIceBerg, data.lots, processingSymbolInfo?.odin_exchange);
          
          const finalData = orderDataFormer({
            data: { ...data, lots: lots },
            token: token,
            orderState: orderState,
          });
          
          await placeOrder(finalData);
        } else {
          // Multiple orders case
          const orderPromises = splittedArray.map((lots) => {
            const finalData = orderDataFormer({
              data: { ...data, lots: `${lots}` },
              token: token,
              orderState: orderState,
            });
            
            // console.log("finalData",finalData.legs)
            return placeOrder(finalData); // Return the promise
          });
          
          // If you want the requests to be executed sequentially, await them in a for loop
          for (let promise of orderPromises) {
            await promise;
          }
          
          // If you want them to run concurrently, use Promise.all
          await Promise.all(orderPromises).then(() => {
            CloseGttModal();
          });
        }
      };
      
      // Check if we need to resolve the symbol first
      if (!currentSymbolInfo?.odin_symbol) {
        DataFeedInstance?.resolveSymbolInfo(
          String(symbol),
          async (resolvedSymbolInfo: ResolveSymbolInfo) => {
            if (resolvedSymbolInfo?.asmGsmStage) {
              // Hide option chain
              var element = document.getElementById("optionChainNew");
              if (element) {
                element.style.display = "none";
              }
              
              // Close the modal
              CloseGttModal();
              
              // Show confirmation dialog
              window.tvWidget.showConfirmDialog({
                title: "Disclaimer",
                body: `Security is under Surveillance Measure - ${resolvedSymbolInfo.asmGsmStage}. Do you wish to continue?`,
                callback: async (confirmed) => {
                  if (confirmed) {
                    // Only process order if user confirms
                    await processOrder(watch("isReducedMargin"), resolvedSymbolInfo);
                  }
                  
                  // Always restore the option chain display
                  var element = document.getElementById("optionChainNew");
                  if (element) {
                    element.style.display = "block";
                    element.style.setProperty("z-index", "100", "important");
                  }
                },
              });
            } else {
              // No surveillance measure, proceed with order
              await processOrder(watch("isReducedMargin"), resolvedSymbolInfo);
            }
          },
          (error) => {
            // console.error("Symbol resolution failed:", error); // Debugging: Catch errors
          },
          ""
        );
      } else {
        // We already have symbol info, check if surveillance confirmation needed
        if (currentSymbolInfo?.asmGsmStage) {
          // Hide option chain
          var element = document.getElementById("optionChainNew");
          if (element) {
            element.style.display = "none";
          }
          
          // Close the modal
          CloseGttModal();
          
          // Show confirmation dialog
          window.tvWidget.showConfirmDialog({
            title: "Disclaimer",
            body: `Security is under Surveillance Measure - ${currentSymbolInfo.asmGsmStage}. Do you wish to continue?`,
            callback: async (confirmed) => {
              if (confirmed) {
                // Only process order if user confirms
                await processOrder(watch("isReducedMargin"), currentSymbolInfo);
              }
              
              // Always restore the option chain display
              var element = document.getElementById("optionChainNew");
              if (element) {
                element.style.display = "block";
                element.style.setProperty("z-index", "100", "important");
              }
            },
          });
        } else {
          // No surveillance measure, proceed with order
          await processOrder(watch("isReducedMargin"), currentSymbolInfo);
        }
      }
    };
    
    // Start the order processing flow - no argument needed
    await processNewOrder();
  };

  const showPriceField = watch("orderType") === "Market";

  const handleMarketDepthOpen = () => {
    if (showPriceField) {
      const data = { open_future_chain: true, future_key: symbol };
      dispatch(OpenFutureChain(data));
      dispatch(setCurrentTab("MarketDepth"));
    }
  };

  useEffect(() => {
    if (summaryData.isVisible) {
      dispatch(
        setChargesSummaryVisibleKey({ key: "price", value: +watch("price") })
      );
      dispatch(
        setChargesSummaryVisibleKey({ key: "quantity", value: +watch("lots") })
      );
      dispatch(
        setChargesSummaryVisibleKey({
          key: "memorizedLTP",
          value: memorizedLtp,
        })
      );
    }
  }, [watch("price"), watch("lots")]);

  const openChargedSumary = () => {
    const lots = +watch("lots");
    const newOrderstate = {
      ...orderState,
      quantity: lots,
      price: (orderState.price ?? 0) * lots,
      isVisible: true,
      instrumentType: ["CE", "PE"].includes(symbolInfo?.odin_option_type ?? "")
        ? "OPTIONS"
        : symbolInfo?.symbol?.slice(symbolInfo?.symbol.length - 3) == "FUT"
        ? "FUTURES"
        : "",
      tradeType: OptionTimeframePayload(orderState.tradeType) as any, // Ensure the correct type if it’s not one of the known values
    };

    dispatch(setChargesSummaryVisible(newOrderstate));

    return () => {
      dispatch(resetChargesSummaryVisible());
    };
  };

  const testData = useMarginsBasket(
    [
      // this is for BuyOrder
      {
        token: token ?? 0,
        exchange: exchange,
        transaction_type: "BUY",
        product: orderState.tradeType,
        variety: "RL-MKT",
        quantity: exchange === "MCX_FO" ? +lots || 1 : (+lots || 1) * lotSize,
        price: ltp ?? 0,
      },
      // this is for Sell order
      {
        token: token,
        exchange: exchange,
        transaction_type: "SELL",
        product: orderState.tradeType,
        variety: getFinalVariety(
          watch("isStopLoss"),
          watch("orderType") === "Market"
        ),
        quantity: exchange === "MCX_FO" ? +lots || 1 : (+lots || 1) * lotSize,
        price: orderState.memorizedLTP,
      },
    ],
    isMarginReductionCheckboxVisible && watch("isReducedMargin")
  );

  // if we had initla margin then
  const initialMargin = testData.data?.initial_margin ?? 0;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="row justify-content-between"
    >
      <div className="row my-5 mx-4 justify-content-between">
        <div className="text-start pe-4 col-6">
          <div className="row mb-5 gx-3">
            <div className="col-md-6">
              <Controller
                name="lots"
                control={control}
                render={({ field }) => (
                  <GenericInput
                    containerClassName="text-start"
                    label="Enter lots"
                    subLable={`1 lot = ${lotSize}`}
                    value={field.value}
                    onChange={field.onChange} // Simplified
                    disabled={false}
                    type="number"
                    id="lots"
                    min={1}
                    error={errors.lots?.message}
                    helperText={
                      <div>
                        <div>
                          Top {tradeDirection === "Buy" ? "Bid" : "Ask"}: {ask}
                        </div>
                        <div>Quantity: {qunatity}</div>
                      </div>
                    }
                  />
                )}
              />
            </div>
            <div className="col-md-6 position-relative">
              <Controller
                name="price"
                control={control}
                render={({ field }) => (
                  <>
                    {showPriceField ? (
                      <div
                        className="position-relative"
                        style={{ opacity: 0.7, cursor: "pointer" }}
                        onClick={handleMarketDepthOpen}
                      >
                        <GenericInput
                          id="price"
                          containerClassName="text-start cursor-pointer"
                          label="Price"
                          subLable={`Tick: ${memerizedTick}`}
                          value={"Market"}
                          onChange={() => {}} // Simplified
                          disabled={true}
                          type="number"
                          step={memerizedTick}
                          error={errors.price?.message}
                          helperText={<div>Price range: {liveQuotes?.dpr}</div>}
                        />
                        <Donut
                          style={{ top: 45, height: 28, width: 28 }}
                          className="position-absolute text-color end-0 pe-2"
                        />
                      </div>
                    ) : (
                      <GenericInput
                        id="price"
                        containerClassName="text-start"
                        label="Price"
                        subLable={`Tick: ${memerizedTick}`}
                        value={field.value}
                        onChange={field.onChange} // Simplified
                        disabled={orderType === "Market"}
                        type="number"
                        step={memerizedTick}
                        error={errors.price?.message}
                        helperText={<div>Price range: {liveQuotes?.dpr}</div>}
                      />
                    )}
                  </>
                )}
              />
            </div>
          </div>

          <div className="d-flex justify-content-between gap-3 align-items-center">
            <div className="custom-switch-wrapper custom-switch-wrapper-justify-between">
              <Controller
                name="isStopLoss"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Form.Check
                    type="switch"
                    id="custom-switch"
                    label="Stop Loss"
                    className="text-end text-color custom-large-switch"
                    checked={value}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                )}
              />
            </div>
            <Controller
              name="orderType"
              control={control}
              render={({ field: { value, onChange } }) => (
                <ToggleButton<OrderType>
                  options={["Limit", "Market"]}
                  selectedOption={value}
                  isDisabled={watch("type") === "Iceberg"}
                  onToggle={(type) => {
                    onChange(type);
                    if (type === "Market") setValue("price", "");
                    // Clear price when Market is selected
                    else setValue("price", memorizedLtp.toFixed(2)); // Clear price when Limit is selected
                  }}
                />
              )}
            />
          </div>

          {isStopLoss && (
            <Controller
              name="triggerPrice"
              control={control}
              render={({ field }) => (
                <GenericInput
                  containerClassName="text-start"
                  label="Trigger Price"
                  id="triggerPrice"
                  value={field.value}
                  onChange={field.onChange} // Simplified
                  type="number"
                  step={memerizedTick}
                  error={errors.triggerPrice?.message}
                />
              )}
            />
          )}

          <strong className="text-color d-block font-14 pt-3 pb-2">
            Validity
          </strong>
          <Controller
            name="validity"
            control={control}
            render={({ field: { value, onChange } }) => (
              <>
                <Button
                  className={`fw-bold font-14 ${
                    value === "DAY" ? "tv-btn-primary" : "tv-btn-secondary"
                  } ${!!thisValidity ? "custom-disabled" : ""}`}
                  disabled={!!thisValidity}
                  onClick={() => onChange("DAY")}
                  size="lg"
                >
                  DAY
                </Button>
                {watch("type") !== "Iceberg" && (
                  <Button
                    className={`fw-bold mx-2 font-14 ${
                      value === "IOC" ? "tv-btn-primary" : "tv-btn-secondary"
                    } ${!!thisValidity ? "custom-disabled" : ""}`}
                    disabled={!!thisValidity}
                    onClick={() => onChange("IOC")}
                    size="lg"
                  >
                    IOC
                  </Button>
                )}
                {!type &&
                  ["CarryForward", "Intraday", "Delivery"].includes(
                    orderState.tradeType
                  ) && (
                    <Button
                      className={`fw-bold font-14 ${
                        value === "AMO" ? "tv-btn-primary" : "tv-btn-secondary"
                      } ${!!thisValidity ? "custom-disabled" : ""}`}
                      disabled={!!thisValidity}
                      onClick={() => onChange("AMO")}
                      size="lg"
                    >
                      AMO
                    </Button>
                  )}
              </>
            )}
          />
          {/* ERROR */}
          <small className="text-candel-red d-block">
            {errors.validity?.message}
          </small>
        </div>
        <div className="text-start ps-4 pe-5 col-6 border-start gtt-border-left">
          {
            <>
              <strong className="text-color d-block font-14 pt-3 pb-2">
                Type
              </strong>
              <Controller
                name="type"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <>
                    {isIceBergVisible && (
                      <Button
                        className={`fw-bold mx-2 font-14 ${
                          value === "Iceberg"
                            ? "tv-btn-primary"
                            : "tv-btn-secondary"
                        } ${
                          !!thisGTTData ||
                          !!thisValidity ||
                          watch("isReducedMargin")
                            ? "custom-disabled"
                            : ""
                        }`}
                        size="lg"
                        onClick={
                          () => {
                            onChange(value === "Iceberg" ? "" : "Iceberg");
                            setValue("orderType", "Limit");
                            setValue("price", memorizedLtp.toFixed(2));
                            setValue("isReducedMargin", false);
                          } // Toggle selection
                        }
                      >
                        Iceberg
                      </Button>
                    )}

                    <Button
                      className={`fw-bold font-14 ${
                        value === "GTT-L"
                          ? "tv-btn-primary"
                          : "tv-btn-secondary"
                      } ${
                        !!thisGTTData ||
                        !!thisValidity ||
                        watch("isReducedMargin")
                          ? "custom-disabled"
                          : ""
                      }`}
                      size="lg"
                      onClick={
                        () => {
                          onChange(value === "GTT-L" ? "" : "GTT-L");
                          setValue("validity", "DAY");
                          setValue("isReducedMargin", false);
                        } // Toggle selection
                      }
                    >
                      GTT-L
                    </Button>

                    <Button
                      className={`fw-bold mx-2 font-14 ${
                        value === "GTT-M"
                          ? "tv-btn-primary"
                          : "tv-btn-secondary"
                      } ${
                        !!thisGTTData ||
                        !!thisValidity ||
                        watch("isReducedMargin")
                          ? "custom-disabled"
                          : ""
                      }`}
                      onClick={() => {
                        onChange(value === "GTT-M" ? "" : "GTT-M"); // Toggle selection
                        setValue("validity", "DAY");
                        setValue("isReducedMargin", false);
                      }}
                      size="lg"
                    >
                      GTT-M
                    </Button>
                  </>
                )}
              />
              {type === "GTT-L" && ( // Conditionally render based on type
                <div className="font-10 pt-2" style={{ color: "#ff992a" }}>
                  {gtt_l_text}
                </div>
              )}
              {/* Stop Loss  |  Target */}
              {type !== "" && watch("type") !== "Iceberg" && (
                <div>
                  <Controller
                    name="gttStoplossValue"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <div className="custom-switch-wrapper pt-4">
                          <Controller
                            name="isGTTStopLoss"
                            control={control}
                            render={({
                              field: {
                                value: isGTTStopLoss,
                                onChange: toggleStopLoss,
                              },
                            }) => (
                              <Form.Check
                                type="switch"
                                id="custom-switch-stoploss"
                                label="Stop Loss %"
                                disabled={!!thisGTTData}
                                className="text-end text-color custom-large-switch pb-1"
                                checked={isGTTStopLoss}
                                onChange={(e) =>
                                  toggleStopLoss(e.target.checked)
                                }
                              />
                            )}
                          />
                        </div>

                        {isGTTStopLoss && (
                          <GenericInput
                            value={value}
                            disabled={!!thisGTTData}
                            onChange={(e) => {
                              let inputValue = e.target.value.trim(); // Remove spaces
                              if (tradeDirection === "Buy") {
                                if (inputValue === "" || isNaN(+inputValue)) {
                                  onChange(""); // Reset if input is empty or invalid
                                } else {
                                  onChange((-Math.abs(+inputValue)).toString()); // Ensure negative value
                                }
                              } else {
                                onChange(inputValue); // Allow normal value for other cases
                              }
                            }}
                            id="gttStoplossValue"
                            type="number"
                            step={memerizedTick}
                            allowNegative
                            error={errors.gttStoplossValue?.message}
                          />
                        )}
                      </>
                    )}
                  />

                  <Controller
                    name="gttTargetValue"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <div className="custom-switch-wrapper pt-4">
                          <Controller
                            name="isGTTTarget"
                            control={control}
                            render={({
                              field: {
                                value: isGTTStopLoss,
                                onChange: toggleStopLoss,
                              },
                            }) => (
                              <Form.Check
                                type="switch"
                                id="custom-switch-"
                                disabled={!!thisGTTData}
                                label="Target %"
                                className="text-end text-color custom-large-switch pb-1"
                                checked={isGTTStopLoss}
                                onChange={(e) =>
                                  toggleStopLoss(e.target.checked)
                                }
                              />
                            )}
                          />
                        </div>

                        {isGTTTarget && (
                          <GenericInput
                            value={value}
                            id="gttTargetValue"
                            disabled={!!thisGTTData}
                            onChange={(e) => {
                              let inputValue = e.target.value.trim(); // Remove spaces
                              if (tradeDirection === "Sell") {
                                if (inputValue === "" || isNaN(+inputValue)) {
                                  onChange(""); // Reset if input is empty or invalid
                                } else {
                                  onChange((-Math.abs(+inputValue)).toString()); // Ensure negative value
                                }
                              } else {
                                onChange(inputValue); // Allow normal value for other cases
                              }
                            }}
                            type="number"
                            step={memerizedTick}
                            allowNegative
                            error={errors.gttTargetValue?.message}
                          />
                        )}
                      </>
                    )}
                  />
                </div>
              )}
              {watch("type") === "Iceberg" && (
                <>
                  <Controller
                    name="numberOfLegs"
                    control={control}
                    render={({ field }) => (
                      <GenericInput
                        disabled={!!thisGTTData}
                        containerClassName="text-start"
                        placeholder="Enter Number of Legs"
                        label="Number of Legs"
                        id="numberOfLegs"
                        value={field.value || ""}
                        onChange={field.onChange} // Simplified
                        type="number"
                        step={memerizedTick}
                        error={errors.triggerPrice?.message}
                      />
                    )}
                  />
                  <small className="text-candel-red d-block">
                    {errors.numberOfLegs?.message}
                  </small>
                </>
              )}
              {/* TODO: need to do work */}
              <small className="text-candel-red d-block">
                {errors.type?.message}
              </small>
            </>
          }
        </div>
      </div>

      <div
        style={{ height: !!isMarginReductionCheckboxVisible ? 80 : 60 }}
        className="px-5 py-2  alerts-create-edit-background"
      >
        {isMarginReductionCheckboxVisible && (
          <ReducedMarginCheckbox
            control={control}
            watch={watch}
            requitedToken={requitedToken}
            symbolInfo={symbolInfo?.odin_market_segment_id}
            onClick={() => {
              setValue("type", "");
            }}
          />
        )}
        <div className="d-flex justify-content-between gap-3">
          <BalanceDisplay
            lots={+lots}
            onClick={openChargedSumary}
            initialMargin={initialMargin}
            isReducedMargin={
              watch("isReducedMargin") && watch("tradeDirection") === "Sell"
            }
            isMarginReductionCheckboxVisible={isMarginReductionCheckboxVisible}
          />
          <ActionButton
            tradeDirection={tradeDirection}
            isEditable={isEditable}
          />
        </div>
      </div>
    </form>
  );
};

export default OrdersBody;
